import React from "react";
import {
  Clock,
  Coffee,
  CalendarCheck,
  UserCheck,
  LogOut,
  Shield,
  AlertTriangle,
  IndianRupee,
  Smartphone,
  Briefcase,
  HelpCircle,
  Edit,
} from "lucide-react";
import "./policy.css";

const Policy = () => {
  const policies = [
    {
      id: 1,
      title: "Office Timings & Work Hours",
      icon: <Clock size={20} />,
      content: (
        <ul className="policy-list">
          <li>
            <strong>Work Days:</strong> Monday to Saturday
          </li>
          <li>
            <strong>Monday to Friday:</strong> 9:30 AM – 6:30 PM
          </li>
          <li>
            <strong>Working Saturdays:</strong> 9:30 AM – 5:30 PM
          </li>
          <li>
            <strong>Holidays:</strong> 2nd and 4th Saturdays & all Sundays
          </li>
          <li>
            <strong>Total Working Hours:</strong> 9 hours per day (including
            breaks)
          </li>
        </ul>
      ),
    },
    {
      id: 2,
      title: "Break Timings",
      icon: <Coffee size={20} />,
      content: (
        <ul className="policy-list">
          <li>
            <strong>Lunch Break:</strong> 1:00 PM – 1:45 PM
          </li>
          <li>
            <strong>Tea/Coffee Breaks:</strong> Two short breaks of 10-15
            minutes are permitted (one in the morning, one in the evening).
          </li>
        </ul>
      ),
    },
    {
      id: 3,
      title: "Leave & Permission Policy",
      icon: <CalendarCheck size={20} />,
      content: (
        <ul className="policy-list">
          <li>
            Employees are eligible for <strong>2 leaves per month</strong>.
          </li>
          <li>Leaves must be approved by your TL and communicated to HR.</li>
          <li>
            If there is no prior information, the absence will be treated as
            Loss of Pay (LOP).
          </li>
          <li>
            Permissions up to 3 hours are allowed per month, in splits, with 1
            hour maximum at a time.
          </li>
          <li>
            Accumulated leaves cannot be carried forward to the next year.
          </li>
          <li>
            Accumulated leave may be taken in bulk, with prior intimation and
            approval.
          </li>
          <li>Leaves taken without balance will be considered LOP.</li>
        </ul>
      ),
    },
    {
      id: 4,
      title: "Probation Policy",
      icon: <UserCheck size={20} />,
      content: (
        <ul className="policy-list">
          <li>
            <strong>Probation Duration:</strong> The probation period will be 2
            months from the date of joining, unless otherwise specified in the
            appointment letter.
          </li>
          <li>
            <strong>Leave During Probation:</strong> Employees are not eligible
            for any leave during the probation period. Any leave or absence
            taken during probation will be treated as Loss of Pay (LOP).
          </li>
        </ul>
      ),
    },
    {
      id: 5,
      title: "Exit / Resignation Policy",
      icon: <LogOut size={20} />,
      content: (
        <ul className="policy-list">
          <li>
            <strong>Notice Period:</strong> Confirmed employees are required to
            serve a 60-day notice period, unless otherwise mentioned in the
            appointment letter.
          </li>
          <li>
            <strong>Resignation Process:</strong> Resignation must be submitted
            via email to the Reporting Manager with HR in copy. The resignation
            will be effective only after formal acceptance by the Company.
          </li>
          <li>
            <strong>Handover of Responsibilities:</strong> Employees must
            complete a proper handover of work, documents, and responsibilities
            before the last working day.
          </li>
          <li>
            <strong>Return of Company Assets:</strong> All company property
            including laptops, ID cards, documents, and other assets must be
            returned before the final settlement.
          </li>
          <li>
            <strong>Final Settlement:</strong> Full and final settlement will be
            processed within 30 days from the employee’s last working day.
          </li>
        </ul>
      ),
    },
    {
      id: 6,
      title: "Code of Conduct",
      icon: <Shield size={20} />,
      content: (
        <div className="policy-nested">
          <div className="policy-nested-section">
            <h4 className="nested-title">Professional Behaviour:</h4>
            <p>
              Employees are expected to maintain high standards of integrity,
              honesty, and professional conduct. Harassment, discrimination, or
              offensive behaviour of any kind will not be tolerated.
            </p>
          </div>
          <div className="policy-nested-section">
            <h4 className="nested-title">Dress Code:</h4>
            <ul className="policy-list">
              <li>
                <strong>Monday to Thursday:</strong> Formals
              </li>
              <li>
                <strong>Fridays & Working Saturdays:</strong> Casuals (No
                shorts, slippers, or offensive slogans allowed)
              </li>
            </ul>
          </div>
          <div className="policy-nested-section">
            <h4 className="nested-title">Office Assets:</h4>
            <p>
              Company laptops, internet, and other resources are for business
              use only. Employees are responsible for the safety and
              confidentiality of company data.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 7,
      title: "Disciplinary Actions",
      icon: <AlertTriangle size={20} />,
      content: (
        <>
          <p className="policy-desc">
            Violation of company policies may lead to disciplinary action,
            structured as follows:
          </p>
          <ul className="policy-list">
            <li>
              <strong>Verbal Warning:</strong> For minor first-time offenses
              (e.g., late coming, minor negligence).
            </li>
            <li>
              <strong>Written Warning:</strong> For repeated violations or
              serious misconduct.
            </li>
            <li>
              <strong>Suspension/Termination:</strong> For gross misconduct
              (e.g., theft, harassment, data breach) or continued violation
              despite written warnings.
            </li>
          </ul>
        </>
      ),
    },
    {
      id: 8,
      title: "Salary",
      icon: <IndianRupee size={20} />,
      content: (
        <p className="policy-desc">
          Salary will be credited between the <strong>6th and 8th</strong> of
          every month.
        </p>
      ),
    },
    {
      id: 9,
      title: "Use of Social Media",
      icon: <Smartphone size={20} />,
      content: (
        <p className="policy-desc">
          Usage of social media sites during office hours is{" "}
          <strong>strictly prohibited</strong>.
        </p>
      ),
    },
    {
      id: 10,
      title: "Overtime Policy",
      icon: <Briefcase size={20} />,
      content: (
        <ul className="policy-list">
          <li>
            <strong>Compensatory Off (Comp-Off):</strong> If an employee works
            on a Weekend or Public Holiday for more than 4 hours, they are
            eligible for a Compensatory Off. This must be utilized within 30
            days.
          </li>
          <li>
            <strong>Approval:</strong> All overtime work must be pre-approved.
          </li>
        </ul>
      ),
    },
    {
      id: 11,
      title: "Grievances & Discrepancies",
      icon: <HelpCircle size={20} />,
      content: (
        <p className="policy-desc">
          Any discrepancies or concerns should be reported to HR, who will
          escalate the matter to Management, if required.
        </p>
      ),
    },
    {
      id: 12,
      title: "Policy Amendments",
      icon: <Edit size={20} />,
      content: (
        <p className="policy-desc">
          The Management reserves the right to amend, alter, delete, or add to
          any of these policies at any time. Any changes will be communicated to
          employees via official email or circulars.
        </p>
      ),
    },
  ];

  return (
    <div className="policy-wrapper">
      <div className="policy-header-banner">
        <div className="policy-header-content">
          <h1 className="policy-title">Company HR Policy</h1>
          <p className="policy-subtitle">
            Comprehensive Guidelines & Employee Information
          </p>
        </div>
      </div>

      <div className="policy-content-container">
        {policies.map((policy) => (
          <div key={policy.id} className="policy-section-card">
            <div className="policy-section-left">
              <div className="policy-icon-box">{policy.icon}</div>
            </div>
            <div className="policy-section-right">
              <h2 className="policy-section-title">
                <span className="policy-section-number">
                  {String(policy.id).padStart(2, "0")}.
                </span>{" "}
                {policy.title}
              </h2>
              <div className="policy-section-body">{policy.content}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Policy;
